import React from 'react';

import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';

type Props = {
  /** 表示テキスト */
  text: string;
  /** 省略する行数（-webkit-line-clamp） */
  lines: number;
} & Omit<TypographyProps, 'children'>;

/**
 * 指定行数で省略表示するテキスト。実際に省略（オーバーフロー）した場合のみ、
 * ホバーで全文をツールチップ表示する。省略していなければツールチップは出さない。
 */
const ClampedText: React.FC<Props> = ({ text, lines, sx, ...rest }) => {
  const ref = React.useRef<HTMLElement | null>(null);
  const [clamped, setClamped] = React.useState(false);

  // 省略されているか（実高さ > 表示高さ）を測定する。テキスト・行数変更時に再判定。
  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, []);

  React.useEffect(() => {
    measure();
  }, [measure, text, lines]);

  // リサイズでも再判定する
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const content = (
    <Typography
      ref={ref}
      {...rest}
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {text}
    </Typography>
  );

  if (!clamped) return content;

  return (
    <Tooltip title={text} placement="top" arrow enterDelay={300}>
      {content}
    </Tooltip>
  );
};

export default ClampedText;
